import { call, select, put, takeEvery, delay } from 'redux-saga/effects';
import getSagaErrorHandler from 'utils/getSagaErrorHandler';
import getData from 'utils/getData';
import isMobileDevice from 'utils/isMobileDevice';
import { AUTH_ROOT, PAGES_ROOT, WORKFLOW_ROOT } from 'config/api';
import { NON_WORKFLOW_PAGES } from 'config/workflow';
import { navigate } from '@reach/router';
import plaidActions from '../plaid/actions';
import workflowActions from '../workflow/actions';
import homeActions from '../home/actions';
import pageActions from './actions';
import groupActions from '../groups/actions';
import dialogActions from '../dialogs/actions';
import analyticsActions from '../analytics/actions';
import dataActions from '../data/actions';

function* pageErrorSaga(e) {
  const {
    response: { config, data: responseData, status: responseStatus },
  } = e;
  const { fields, page } = yield select();
  if (responseStatus !== 400) {
    yield put(pageActions.goToPageError(responseData, responseStatus));
    return;
  }

  if (responseStatus === 400 && config.method === 'get') {
    yield put(pageActions.goToPageSuccess(responseData));
    yield put(pageActions.nextFailed(responseData, fields));
    return;
  }
  const hasPageName = Boolean(page.name);
  const hasValidData = responseData.data && responseData.meta;
  const hasResultMsgs = Boolean(responseData.result?.msgs);

  if (!hasPageName && !hasValidData) {
    yield put(pageActions.goToPageError(responseData, responseStatus));
    return;
  }

  if (!hasPageName) {
    yield put(pageActions.goToPageSuccess(responseData));
  }

  if (hasPageName || hasResultMsgs) {
    yield put(pageActions.nextFailed(responseData, fields));
  }
}

export const wrapPageSagaWithErrorHandler = getSagaErrorHandler(pageErrorSaga);

function* getPage({ payload }) {
  if (!payload.data.meta.nextPage) {
    return;
  }
  const request = pageActions.getRequest(payload.data.meta.nextPage.name);
  const response = yield call(request);
  const { config, data } = response;
  if (config.method === 'get' && data?.meta?.nextPage) {
    yield put(pageActions.nextSuccess(data));
  } else {
    yield put(pageActions.goToPageSuccess(data));
  }
}

function* getNonWorkFlowPage({ payload }) {
  if (!payload.data) {
    return;
  }

  const request = pageActions.getRequest(payload.data);
  const response = yield call(request);
  const { data } = response;
  yield put(pageActions.goToPageSuccess(data));
}

function* acceptAddress({ payload }) {
  const { data: stateData, page } = yield select();
  const response = yield call(payload.request(page, stateData.addressType));
  const { data } = response;
  const isMobile = isMobileDevice();

  yield put(pageActions.goToPageSuccess(data, isMobile, isMobile));
  yield put(dialogActions.close());
}

function* mobileValidationSubmit({ payload }) {
  const { page, ui } = yield select();
  const isMobile = isMobileDevice();
  const isUserAuth = page.name === 'userauth';
  const { verificationCode } = payload;
  const endpoint = isUserAuth
    ? `${AUTH_ROOT}/actions/verifyCode`
    : `${PAGES_ROOT}/${page.name}/actions/verifyOTP`;
  const postData = isUserAuth ? { authCode: verificationCode } : { verificationCode };
  try {
    const response = yield call(payload.request(endpoint, postData));
    if (isUserAuth) {
      yield put(pageActions.nextSuccess(response.data));
    } else if (isMobile) {
      yield put(dialogActions.close());
      yield put(groupActions.next(ui.activeGroup));
    } else {
      yield put(dialogActions.close());
      yield put(pageActions.next());
    }
  } catch (e) {
    const { response } = e;
    if (response.status === 400) {
      if (isUserAuth && response.data?.result?.msgs['*_USER_AUTH']) {
        yield put(groupActions.nextFailed(response.data));
      } else {
        yield put(pageActions.mobileValidationFailed(response.data?.result?.msgs['*']));
      }
    } else {
      yield put(pageActions.goToPageError(response.data, response.status));
    }
  }
}

function* mobileValidationResend({ payload }) {
  const { page } = yield select();
  const isUserAuth = page.name === 'userauth';
  const endpoint = isUserAuth
    ? `${AUTH_ROOT}/actions/resendCode`
    : `${PAGES_ROOT}/${page.name}/actions/resendVerificationCode`;
  try {
    const response = yield call(payload.request(endpoint));
    if (response && response.data && response.data.result.msgs) {
      yield put(pageActions.mobileValidationSuccess(response.data.result.msgs['*']));
    }
  } catch (e) {
    const { response } = e;
    if (response && response.data && response.data.result.msgs) {
      const msg = response.data.result.msgs['*'] || response.data.result.msgs.MAX_ATTEMPTS;
      const disableResend = !!response.data.result.msgs.MAX_ATTEMPTS;
      yield put(pageActions.mobileValidationFailed(msg, disableResend));
    }
  }
}

function* start({ payload }) {
  const { page } = yield select();
  try {
    yield call(payload.request(page));
    yield put(pageActions.startSuccess(page));
    yield put(pageActions.status());
  } catch (e) {
    yield put(pageActions.startFailed(e));
  }
}

function* status({ payload }) {
  const { page } = yield select();
  const response = yield call(payload.request(page.name));
  const { data } = response;
  const { autoExportStatus, kbaFetchOrSubmitStatus, qualifyStatus } = data.data;
  if (qualifyStatus || autoExportStatus) {
    const pageStatus = qualifyStatus || autoExportStatus;
    if (pageStatus !== 'IN_PROGRESS') {
      yield put(pageActions.next({ isStatus: true }));
    } else {
      yield delay(2000);
      yield put(pageActions.status());
    }
  } else if (kbaFetchOrSubmitStatus) {
    if (
      (data.data.questions.length > 0 && !payload.kbaSubmit) ||
      (data.data.questions.length > 0 &&
        payload.kbaSubmit &&
        kbaFetchOrSubmitStatus === 'SUCCESS_WITH_QUESTIONS')
    ) {
      yield put(pageActions.goToPageSuccess(data));
    } else if (
      (kbaFetchOrSubmitStatus === 'SUCCESS' && payload.kbaSubmit) ||
      kbaFetchOrSubmitStatus === 'ERROR' ||
      kbaFetchOrSubmitStatus === 'DECLINED' ||
      kbaFetchOrSubmitStatus === 'SKIP' ||
      kbaFetchOrSubmitStatus === 'TIMEOUT'
    ) {
      yield put(pageActions.next({ isStatus: true }));
    } else {
      yield delay(2000);
      yield put(pageActions.status('kba/questions', payload.kbaSubmit));
    }
  }
}

function* back({ payload }) {
  const { page } = yield select();
  const response = yield call(payload.request(page.name));
  const { data } = response;
  yield put(pageActions.backSuccess(data));
}

const getEsignPageData = (data, groups) =>
  Object.keys(groups).reduce((pageData, groupKey) => {
    const { fields } = groups[groupKey];
    if (fields) {
      return {
        ...pageData,
        ...fields.reduce(
          (groupData, fieldKey) => ({
            ...groupData,
            [fieldKey]: data[fieldKey],
          }),
          {},
        ),
      };
    }
    return pageData;
  }, {});

const getKbaPageData = (data, fields) => ({
  questions: Object.keys(fields).map((key) => {
    const lookups = fields[key].lookupValues;
    return {
      answerId: data[fields[key].clientId],
      answers: Object.keys(lookups).map((lkey) => ({
        correct: false,
        id: lkey,
        valid: null,
        value: lookups[lkey],
      })),
      id: fields[key].id,
      value: fields[key].label,
    };
  }),
});

function* next({ payload }) {
  const { data: formData, fields, groups, page, session } = yield select();
  let submitData = formData;
  let { name: pageName } = page;
  if (payload.isStatus) {
    submitData = {};
  } else if (page.name === 'kba') {
    submitData = getKbaPageData(formData, fields);
    pageName = 'kba/questions';
  } else if (page.name === 'esign') {
    submitData = getEsignPageData(formData, groups);
  }

  const path = !NON_WORKFLOW_PAGES.includes(pageName)
    ? `${PAGES_ROOT}/${pageName}`
    : `${WORKFLOW_ROOT}/${pageName}`;

  let token;
  if (page.recaptchaEnabled && window.grecaptcha) {
    token = yield call(() =>
      window.grecaptcha.execute(session.meta.configs.google_recaptcha_site_key, {
        action: 'page_submit',
      }),
    );
  }

  const response = yield call(
    payload.request(path, token ? { ...submitData, appRequest_recaptcha: token } : submitData),
  );
  const { data } = response;
  if (pageName === 'kba/questions') {
    yield put(pageActions.status('kba/questions', true));
  } else {
    yield put(pageActions.nextSuccess(data));
  }
}

function* documentUploadNext({ payload }) {
  yield call(payload.request);
  yield put(pageActions.documentUploadSuccess());
}

function* getCurrentPage() {
  const { page } = yield select();
  const request = pageActions.getRequest(page.name);
  const response = yield call(request);
  const { data } = response;
  yield put(pageActions.goToPageSuccess(data));
}

function* saveSignature({ payload }) {
  try {
    yield call(payload.request);
    yield put(pageActions.saveSignatureSuccess(payload.id));
  } catch (e) {
    const errorMsg =
      e.response?.data?.result?.msgs?.['*'] || 'An error occurred while saving signature';
    yield put(pageActions.saveSignatureError(errorMsg));
  }
}

function* saveBeneficiary({ payload }) {
  const { page } = yield select();
  const formData = yield select(getData);
  try {
    yield call(payload.request(formData, page.name));
    yield put(
      dataActions.changeListGroupData('BENEFICIARIES___list', payload.index, 'isEditing', false),
    );
    yield put(
      dataActions.changeListGroupData('BENEFICIARIES___list', payload.index, 'isNew', false),
    );
    yield put(pageActions.saveBeneficiarySuccess());
  } catch (e) {
    const { response } = e;
    if (response && response.data) {
      yield put(groupActions.nextFailed(response.data));
    }
  }
}

function* handleListGroupAction({ payload }) {
  const { page } = yield select();
  const formData = yield select(getData);
  try {
    const response = yield call(payload.request(formData, page.name));
    const { data } = response;
    if (data.meta.nextPage) {
      yield put(pageActions.continueUserSuccess(data));
    } else {
      yield put(pageActions.goToPageSuccess(data, false));
      window.scrollTo(0, 0);
    }
  } catch (e) {
    const { response } = e;
    if (response && response.status === 400) {
      if (response.data) {
        yield put(groupActions.nextFailed(response.data));
      }
      if (response.status) {
        if (response.data.result) {
          // Verification questions
          if (
            response.data.result.msgs &&
            response.data.result.msgs['*'] &&
            response.data.result.msgs['*'] === 'ANSWER_VERIFICATION_QUESTIONS'
          ) {
            // TODO: FIX THIS
            // if (view) {
            //   view.handleAnswerVerificationQuestions(rs.responseJSON);
            // }
          } else {
            yield put(groupActions.nextFailed(response.data));
          }
        }
      }
    } else {
      yield put(pageActions.goToPageError(response.data, response.status));
    }
    window.scrollTo(0, 0);
  }
}

function* logout({ payload }) {
  const { redirectUrl } = payload;
  yield call(payload.request);
  window.location.assign(redirectUrl || '/oao/userauth');
}

function* save({ payload }) {
  const { page } = yield select();
  const formData = yield select(getData);
  yield call(payload.request(page.name, formData));
  yield put(pageActions.goToNonWorkflowPage('home'));
}

function* continueJoint({ payload }) {
  const response = yield call(payload.request);
  const { data } = response;
  yield put(pageActions.continueJointSuccess(data));
}

function* inviteJoint({ payload }) {
  const { configs, resources } = yield select();
  yield call(payload.request);
  if (configs.isMessageDisclosureEnabled) {
    yield put(pageActions.logout(resources.logoutRedirectUrl));
  } else {
    yield put(pageActions.goToNonWorkflowPage('home'));
  }
}

function* verifyFunding({ payload }) {
  try {
    const { data: formData, dialogs, groups } = yield select();
    const { fundingVerificationId, id } = dialogs.params.request;
    const submitData = groups.HOME_FUNDING_VERIFY.fields.reduce(
      (p, clientId) => ({
        ...p,
        [clientId]: formData[clientId],
      }),
      {},
    );
    yield call(payload.request(submitData, id, fundingVerificationId));

    yield put(
      workflowActions.restart({
        appRequestId: id,
        ddaAutoExportRequired: true,
        errorHandler: pageActions.verifyFundingSuccess(),
      }),
    );
  } catch (e) {
    const { response } = e;
    if (response.status === 400) {
      yield put(pageActions.verifyFundingFailed(response.data));
    } else {
      yield put(pageActions.goToPageError(response.data, response.status));
    }
  }
}

function* postProcessPageLoad({ payload }) {
  try {
    const { refs, session } = yield select();
    const {
      data: {
        meta: {
          page: { name },
        },
      },
    } = payload;
    const { market, product } = refs;
    // eslint-disable-next-line camelcase
    const dynamicDonePages = session?.configs?.dynamic_done_pages?.split(',') ?? [];

    yield put(analyticsActions.oaoPage(name));

    if (name === 'done' || dynamicDonePages.includes(name)) {
      yield put(analyticsActions.oaoWorkflowEnd(product));
    }

    if (name === 'userauth' && market) {
      // This is currently the only wait to retain market information on userauth page
      yield window.history.replaceState(null, null, `?market=${market}`);
    }
  } catch (e) {
    // Do nothing
  }
}

export default [
  takeEvery(pageActions.BACK, wrapPageSagaWithErrorHandler(back)),
  takeEvery(
    [
      homeActions.START_SUCCESS,
      workflowActions.START_SUCCESS,
      workflowActions.RESTART_SUCCESS,
      groupActions.NEXT_SUCCESS,
      groupActions.UPDATE_SUCCESS,
      pageActions.BACK_SUCCESS,
      pageActions.NEXT_SUCCESS,
      pageActions.CONTINUE_JOINT_SUCCESS,
      pageActions.CONTINUE_USER_SUCCESS,
      plaidActions.FETCH_ACCOUNTS_SUCCESS,
    ],
    wrapPageSagaWithErrorHandler(getPage),
  ),
  takeEvery(pageActions.ACCEPT_ADDRESS, wrapPageSagaWithErrorHandler(acceptAddress)),
  takeEvery(pageActions.MOBILE_VALIDATION_SUBMIT, mobileValidationSubmit),
  takeEvery(
    pageActions.MOBILE_VALIDATION_RESEND,
    wrapPageSagaWithErrorHandler(mobileValidationResend),
  ),
  takeEvery(pageActions.START, wrapPageSagaWithErrorHandler(start)),
  takeEvery(pageActions.STATUS, wrapPageSagaWithErrorHandler(status)),
  takeEvery(pageActions.GO_NON_WORKFLOW, wrapPageSagaWithErrorHandler(getNonWorkFlowPage)),
  takeEvery(pageActions.NEXT, wrapPageSagaWithErrorHandler(next)),
  takeEvery(pageActions.DOCUMENT_UPLOAD_NEXT, wrapPageSagaWithErrorHandler(documentUploadNext)),
  takeEvery(
    [pageActions.DOCUMENT_UPLOAD_NEXT_SUCCESS, pageActions.GET_CURRENT],
    wrapPageSagaWithErrorHandler(getCurrentPage),
  ),
  takeEvery(pageActions.SAVE_SIGNATURE, saveSignature),
  takeEvery(
    [
      pageActions.ADD_BENEFICIARY,
      pageActions.REMOVE_BENEFICIARY,
      pageActions.ADD_SIGNER_STATUS,
      pageActions.ADD_SIGNER_STATUS_BACK,
      pageActions.CONTINUE_USER,
      pageActions.REMOVE_PRODUCT,
      pageActions.REMOVE_SIGNER,
      pageActions.RESEND_EMAIL,
      pageActions.SAVE_SIGNERS,
    ],
    handleListGroupAction,
  ),
  takeEvery(pageActions.SAVE_BENEFICIARY, saveBeneficiary),
  takeEvery(pageActions.LOGOUT, wrapPageSagaWithErrorHandler(logout)),
  takeEvery(pageActions.SAVE, wrapPageSagaWithErrorHandler(save)),
  takeEvery(pageActions.CONTINUE_JOINT, wrapPageSagaWithErrorHandler(continueJoint)),
  takeEvery(pageActions.INVITE_JOINT, wrapPageSagaWithErrorHandler(inviteJoint)),
  takeEvery(pageActions.VERIFY_FUNDING, verifyFunding),
  takeEvery(pageActions.GO_SUCCESS, postProcessPageLoad),
];
